/**
 * FeatureCardsGrid — secondary cards (Messages, Community, Scanner, Photos, PreachKit, Streaming).
 * Each card either navigates to a route OR triggers a dialog via onAction.
 */
import { motion } from "framer-motion";
import {
  MessageSquare, Users, ScanLine, Camera, ScrollText, Monitor, Sparkles,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export const FEATURE_CARDS = [
  { id: "messages",   titleKey: "messages",          descKey: "messages_desc",          icon: MessageSquare, color: "bg-rose-500",   route: "/messages",   showBadge: true },
  { id: "community",  titleKey: "community",         descKey: "community_desc",         icon: Users,         color: "bg-pink-500",   route: "/community" },
  { id: "scanner",    titleKey: "document_scanner",  descKey: "scanner_desc",           icon: ScanLine,      color: "bg-cyan-500",   route: "/scanner" },
  { id: "photos",     titleKey: "photo_enhancement", descKey: "photo_enhancement_desc", icon: Camera,        color: "bg-violet-500", route: "/community" },
  { id: "preach-kit", titleKey: "preach_kit",        descKey: "preach_kit_desc",        icon: ScrollText,    color: "bg-amber-500",  route: "/preach-kit", badge: "BETA" },
  { id: "streaming",  titleKey: "live_streaming",    descKey: "live_streaming_desc",    icon: Monitor,       color: "bg-red-500",    action: "streaming" },
];

export default function FeatureCardsGrid({ t, onAction, unreadDMCount = 0 }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-10"
    >
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5" />
        {t("features") || "Features"}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {FEATURE_CARDS.map((card, idx) => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + idx * 0.05 }}
          >
            <Card
              className="cursor-pointer hover:shadow-md transition-all hover:border-slate-300 dark:hover:border-slate-600"
              onClick={() => onAction(card)}
              data-testid={`feature-${card.id}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center flex-shrink-0 relative`}>
                  <card.icon className="w-5 h-5 text-white" />
                  {card.showBadge && unreadDMCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1 animate-pulse">
                      {unreadDMCount > 99 ? "99+" : unreadDMCount}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="font-medium text-sm">{t(card.titleKey)}</h4>
                  <p className="text-xs text-muted-foreground truncate">{t(card.descKey)}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}
